import React, { useEffect, useState } from "react";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Calendar } from "../../../../themes/Default/components/Calendar";
import { Month } from "../../../../themes/Default/components/Calendar/components/Month";
import { Day, DayOffset } from "../../../../themes/Default/components/Calendar/components/Month/components/Day";
import { CalendarFeatured } from "../../../../themes/Default/components/CalendarFeatured";
import { SideMenu } from "../../../../themes/Default/components/CalendarFeatured/components/SideMenu";
import { DailyEvents } from "../../../../themes/Default/components/CalendarFeatured/components/DailyEvents";
import { Event } from "../../../../themes/Default/components/CalendarFeatured/components/DailyEvents/components/Event";
import { useNavigate } from "react-router-dom";
import { eachDayOfInterval, endOfMonth, format, isSameDay, startOfMonth } from "date-fns";
import { calendarManagement } from "../../../../hooks/api/CalendarManagement";
import { useTranslation } from "react-i18next";

const DayView = () => {
    const { t } = useTranslation("common");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const days = eachDayOfInterval({
        start: startOfMonth(currentMonth),
        end: endOfMonth(currentMonth),
    });
    const navigate = useNavigate();
    const { useCalendarListMutation } = calendarManagement;
    const [calendarList] = useCalendarListMutation();
    const [eventsByDay, setEventsByDay] = useState([]);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [selectedDate, setSelectedDate] = useState(new Date());
    
    const tabs = [
        { title: t("dayView"), isActive: true},
        { title: t("weekView"), onClick: () => gotoWeekView()},
    ];

    const gotoWeekView = () => {
        navigate("/calendar-featured/week-view");
    };

    const gotoAddEvent = () => {
        navigate("/calendar/add-event");
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() - 1);
            return newDate;
        });
    };
    
    const goToNextMonth = () => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + 1);
            return newDate;
        });
    };

    const handleDayClick = (date) => {
        setSelectedDate(date);
    };

    useEffect(() => {
        const fetchDayEvents = async () => {
            const formattedDate = format(selectedDate, "yyyy-MM-dd");
            const response = await calendarList({
                dateFrom: formattedDate,
                dateTo: formattedDate,
            }).unwrap();
    
            setEventsByDay(response.records?.[formattedDate] || []);
        };
    
        fetchDayEvents();
    }, [selectedDate]);

    useEffect(() => {
        const timeToMinutes = (time) => {
            const [hour, minutes] = time.split(":").map(Number);
            return hour * 60 + minutes;
        };
    
        const calc15MinBlocks = (start, end) => {
            return Math.ceil((timeToMinutes(end) - timeToMinutes(start)) / 15);
        };
    
        const wraps = document.querySelectorAll(".calendar-event-wrap[data-start][data-end]");
        wraps.forEach((wrap) => {
            const { start, end } = wrap.dataset;
            const blocks = calc15MinBlocks(start, end);
            wrap.style.height = `calc(var(--row-height) * ${blocks})`;
        });
    }, [eventsByDay]);

    useEffect(() => {
        const handleDocumentClick = (e) => {
            const clickedWrap = e.target.closest(".calendar-event-wrap");
            const allWraps = document.querySelectorAll(".calendar-event-wrap");
    
            if (clickedWrap) {
                const isAlreadyActive = clickedWrap.classList.contains("is-active");
    
                allWraps.forEach((w) => w.classList.remove("is-active"));
    
                if (!isAlreadyActive) {
                    clickedWrap.classList.add("is-active");
                }
    
                e.stopPropagation();
            } else {
                allWraps.forEach((w) => w.classList.remove("is-active"));
            }
        };
    
        document.addEventListener("click", handleDocumentClick);
        return () => {
            document.removeEventListener("click", handleDocumentClick);
        };
    }, []);

    return (
        <DefaultLayout>
            <TopNavigation>
                <Button title={t("calendar.addEvent")} icon="fa-regular fa-plus" onClick={gotoAddEvent}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main> 
                <CalendarFeatured>
                    <SideMenu>
                        <Calendar 
                            hasArrow={true}
                            onPrev={goToPreviousMonth} 
                            onNext={goToNextMonth}
                        >
                            <Month
                                month={monthNames[currentMonth.getMonth()]}
                                year={currentMonth.getFullYear()}
                                isWeek={true}
                            >
                                <DayOffset days={days} />
                                {days.map((day, index) => {
                                    const isWeekend = day.getDay() === 6 || day.getDay() === 0;
                                    const isSelected = isSameDay(day, selectedDate);
                                    const isToday = isSameDay(day, new Date());

                                    return (
                                        <Day
                                            key={index}
                                            isWeekend={isWeekend}
                                            isSelected={isSelected}
                                            isToday={isToday}
                                            value={format(day, "d")}
                                            onClick={() => handleDayClick(day)}
                                        />
                                    );
                                })}
                            </Month>
                        </Calendar>
                    </SideMenu>
                    <DailyEvents>
                        {Array.from({ length: 16 }, (_, i) => {
                            const hour = 7 + i;
                            return (
                                <Event key={hour} hour={hour} events={eventsByDay} />
                            );
                        })}
                    </DailyEvents>
                </CalendarFeatured>
            </Main> 
        </DefaultLayout>
    );
};

export default DayView;
